/**
 * 收起 icon 能力：B 端配置 / 预览共用（尺寸、关闭按钮回显）
 * 与 C 端 iconFitSize 规则对齐，但不依赖 react-plugin
 */

export const ICON_CLOSE_SIZE_OPTIONS = [10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30];

export const ICON_CLOSE_POSITION_OPTIONS = [
  { label: '左上', value: 'LeftTop' },
  { label: '右上', value: 'RightTop' },
  { label: '左下', value: 'LeftBottom' },
  { label: '右下', value: 'RightBottom' }
];

export const DEFAULT_ICON_CLOSE_COLOR = 'rgba(255, 255, 255, 1)';
/** 关闭按钮背景默认：#000 70% 透明 */
export const DEFAULT_ICON_CLOSE_BG_COLOR = 'rgba(0, 0, 0, 0.7)';
/** 灰度缺省 / 新建默认关闭尺寸 */
export const DEFAULT_ICON_CLOSE_SIZE = 16;
/** 早期 ensure/默认下拉误写入的关闭尺寸，不能当作用户配置 */
export const LEGACY_DEFAULT_ICON_CLOSE_SIZE = 18;
/** 早期默认关闭背景（污染识别兼容） */
const LEGACY_DEFAULT_ICON_CLOSE_BG_COLORS = [
  'rgba(0,0,0,0.7)',
  'rgba(51,51,51,1)'
];

function normalizeCssColor(color?: string): string {
  return (color || '').replace(/\s+/g, '').toLowerCase();
}

function isDefaultIconCloseBgColor(color?: string): boolean {
  if (!color) {
    return true;
  }
  const normalized = normalizeCssColor(color);
  return LEGACY_DEFAULT_ICON_CLOSE_BG_COLORS.some((c) => normalized === c);
}

const SQUARE_SIZE_MAP: Record<string, number> = {
  Small: 30,
  Medium: 50,
  Big: 80
};

const DEVICE_SIZE_MAP: Record<string, { phone: number; desktop: number }> = {
  Small: { phone: 28, desktop: 40 },
  Medium: { phone: 35, desktop: 50 },
  Big: { phone: 46, desktop: 65 }
};

export function isOriginalIconFit(mode?: string): boolean {
  return mode === 'original';
}

export function getSquareIconWidth(iconSizeType?: string): number {
  return SQUARE_SIZE_MAP[iconSizeType || ''] || SQUARE_SIZE_MAP.Medium;
}

function getDeviceSize(iconSizeType: string | undefined, isPhone: boolean): number {
  const key = iconSizeType === 'Small' || iconSizeType === 'Big' ? iconSizeType : 'Medium';
  const map = DEVICE_SIZE_MAP[key];
  return isPhone ? map.phone : map.desktop;
}

export function snapIconCloseSizeToEnum(
  size: number,
  options: number[] = ICON_CLOSE_SIZE_OPTIONS
): number {
  let best = options[0];
  for (let i = 0; i < options.length; i++) {
    const o = options[i];
    const d = Math.abs(o - size);
    const bd = Math.abs(best - size);
    if (d < bd || (d === bd && o > best)) {
      best = o;
    }
  }
  return best;
}

/** 线上关闭尺寸：盒宽 × 30% */
export function calcOnlineIconCloseSize(boxWidth: number): number {
  return Math.max(1, Math.round((boxWidth || 50) * 0.3));
}

/**
 * 是否存在「明确的」关闭高级配置（样式/位置/颜色）。
 * 仅有 iconCloseSize（如早期误写入的默认 18）不算，避免污染存量回显。
 */
export function hasExplicitIconCloseConfig(position?: {
  iconCloseStyle?: string;
  iconClosePosition?: string;
  iconCloseColor?: string;
  iconCloseBgColor?: string;
}): boolean {
  if (!position) {
    return false;
  }
  return !!(
    position.iconCloseStyle ||
    position.iconClosePosition ||
    position.iconCloseColor ||
    position.iconCloseBgColor
  );
}

/**
 * 早期 ensure 写入的污染：size=18，且颜色仍是默认（或未写）。
 * 图片外 / 图片内叠加均不应信任该尺寸，回退默认 16。
 */
export function isLegacyPollutedIconCloseSize(position?: {
  iconCloseSize?: number;
  iconCloseStyle?: string;
  iconClosePosition?: string;
  iconCloseColor?: string;
  iconCloseBgColor?: string;
}): boolean {
  if (!position || position.iconCloseSize !== LEGACY_DEFAULT_ICON_CLOSE_SIZE) {
    return false;
  }
  if (
    position.iconCloseColor &&
    normalizeCssColor(position.iconCloseColor) !== normalizeCssColor(DEFAULT_ICON_CLOSE_COLOR)
  ) {
    return false;
  }
  if (
    position.iconCloseBgColor &&
    !isDefaultIconCloseBgColor(position.iconCloseBgColor)
  ) {
    return false;
  }
  return true;
}

/** 应清掉的误写尺寸：早期默认 18 污染 */
export function shouldClearPollutedIconCloseSize(position?: {
  iconCloseSize?: number;
  iconCloseStyle?: string;
  iconClosePosition?: string;
  iconCloseColor?: string;
  iconCloseBgColor?: string;
}): boolean {
  return isLegacyPollutedIconCloseSize(position);
}

/** 可信任的落库尺寸：用户配置的 10～30，排除早期默认 18 污染 */
export function getTrustedIconCloseSize(position?: {
  iconCloseSize?: number;
  iconCloseStyle?: string;
  iconClosePosition?: string;
  iconCloseColor?: string;
  iconCloseBgColor?: string;
}): number | undefined {
  if (!position || isLegacyPollutedIconCloseSize(position)) {
    return undefined;
  }
  const size = position.iconCloseSize;
  if (size && size >= 10 && size <= 30) {
    return size;
  }
  return undefined;
}

/** 下拉回显：优先用户配置；缺省/新建默认 16 */
export function resolveDisplayIconCloseSize(
  position: {
    iconCloseSize?: number;
    iconCloseStyle?: string;
    iconClosePosition?: string;
    iconCloseColor?: string;
    iconCloseBgColor?: string;
  } | undefined,
  _boxWidth: number,
  options: number[] = ICON_CLOSE_SIZE_OPTIONS
): number {
  const trusted = getTrustedIconCloseSize(position);
  if (trusted != null) {
    return options.indexOf(trusted) >= 0 ? trusted : snapIconCloseSizeToEnum(trusted, options);
  }
  return DEFAULT_ICON_CLOSE_SIZE;
}

/** 预览/渲染用关闭尺寸：优先用户配置；缺省/新建默认 16 */
export function resolveIconCloseRenderSize(
  position: {
    iconCloseSize?: number;
    iconCloseStyle?: string;
    iconClosePosition?: string;
    iconCloseColor?: string;
    iconCloseBgColor?: string;
  } | undefined,
  _boxWidth: number
): number {
  const trusted = getTrustedIconCloseSize(position);
  if (trusted != null) {
    return trusted;
  }
  return DEFAULT_ICON_CLOSE_SIZE;
}

export function resolveIconClosePosition(iconPosition?: string, configured?: string): string {
  if (configured) {
    return configured;
  }
  const isRight = ['RightTop', 'RightMiddle', 'RightBottom'].includes(iconPosition || '');
  return isRight ? 'LeftTop' : 'RightTop';
}

/** 预览 icon 盒尺寸（与 C calcIconBoxSize 对齐） */
export function calcPreviewIconBoxSize(options: {
  iconFitMode?: string;
  iconSizeType?: string;
  isPhone?: boolean;
  /** 推荐比例下线上 iconSize.width（含竖图动态） */
  squareWidth?: number;
  naturalWidth?: number;
  naturalHeight?: number;
}): { width: number; height: number } {
  const sizeType = options.iconSizeType || 'Medium';
  const isPhone = !!options.isPhone;

  if (isOriginalIconFit(options.iconFitMode)) {
    const height = getDeviceSize(sizeType, isPhone);
    const naturalW = options.naturalWidth || 1;
    const naturalH = options.naturalHeight || 1;
    const width = Math.max(1, Math.round(height * (naturalW / naturalH)));
    return { width, height };
  }

  const base = Number(options.squareWidth) || getSquareIconWidth(sizeType);
  return { width: base, height: base };
}

/** inside 关闭钮角 transform */
export function getInsideCloseTransform(pos: string): string | undefined {
  const map: Record<string, string> = {
    LeftTop: 'translate(-50%, -50%)',
    RightTop: 'translate(50%, -50%)',
    LeftBottom: 'translate(-50%, 50%)',
    RightBottom: 'translate(50%, 50%)'
  };
  return map[pos];
}

/** B 预览：外层贴边盒样式（推荐比例外层宽高与 master 一致：均为 width） */
export function buildPackUpOuterStyle(opts: {
  box: { width: number; height: number };
  fitMode?: string;
  backgroundColor?: string;
  iconStyles: Record<string, any>;
  position: string;
  iconCustom?: boolean;
  iconCustomBottom?: string | number;
  iconCustomBottomUnit?: string;
}): Record<string, string | undefined> {
  const isOriginal = isOriginalIconFit(opts.fitMode);
  const posStyle = opts.iconStyles[opts.position] || {};
  return {
    width: `${opts.box.width}px`,
    // 推荐比例：与 master 相同，外层固定为正方形（宽=高=width）
    // original：定高，宽度随比例
    height: isOriginal ? `${opts.box.height}px` : `${opts.box.width}px`,
    minHeight: isOriginal ? `${opts.box.height}px` : undefined,
    // 竖图 / 外侧关闭会超出正方形，需可见溢出（master 未裁切）
    overflow: 'visible',
    backgroundColor: opts.backgroundColor || 'rgba(0, 0, 0, 0)',
    top: !opts.iconCustom ? posStyle.top || '' : undefined,
    bottom: opts.iconCustom
      ? `${opts.iconCustomBottom}${opts.iconCustomBottomUnit || 'px'}`
      : posStyle.bottom || '',
    left: posStyle.left || '',
    right: posStyle.right
  };
}

/** B 预览：内层图片容器（推荐比例对齐 master：只限制 maxW/maxH，竖图 maxH 放开） */
export function buildPackUpInnerStyle(opts: {
  box: { width: number; height: number };
  fitMode?: string;
  iconDynamicSizing?: boolean;
  iconPosition?: string;
}): Record<string, string | undefined> {
  const isOriginal = isOriginalIconFit(opts.fitMode);
  const isRight = ['RightTop', 'RightMiddle', 'RightBottom'].includes(opts.iconPosition || '');
  if (isOriginal) {
    return {
      width: `${opts.box.width}px`,
      maxWidth: 'none',
      maxHeight: 'none',
      height: `${opts.box.height}px`,
      minHeight: `${opts.box.height}px`,
      overflow: 'visible',
      position: 'relative',
      textAlign: isRight ? 'left' : 'right'
    };
  }
  // 推荐比例：与 master 一致，不写死 width/height，避免 flex 布局把竖图高度压扁
  return {
    maxWidth: `${opts.box.width}px`,
    maxHeight: opts.iconDynamicSizing ? 'none' : `${opts.box.width}px`,
    height: 'auto',
    overflow: 'visible',
    position: 'relative',
    textAlign: isRight ? 'left' : 'right'
  };
}

/** B 预览：img（推荐比例对齐 master；竖图不限制 maxHeight） */
export function buildPackUpImgStyle(opts: {
  box: { width: number; height: number };
  fitMode?: string;
  iconDynamicSizing?: boolean;
}): Record<string, string | number | undefined> {
  if (isOriginalIconFit(opts.fitMode)) {
    return {
      height: `${opts.box.height}px`,
      minHeight: `${opts.box.height}px`,
      width: 'auto',
      maxWidth: 'none',
      maxHeight: 'none',
      flexShrink: 0,
      display: 'block',
      objectFit: 'contain'
    };
  }
  return {
    maxWidth: `${opts.box.width}px`,
    maxHeight: opts.iconDynamicSizing ? 'none' : `${opts.box.width}px`,
    height: 'auto',
    flexShrink: 0,
    display: 'block'
  };
}
